import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, X, Loader2 } from "lucide-react";
import { uploadImage, validateImageFile } from "../../lib/uploadImage";

/**
 * MultiImageUploader — compact image uploader for user-generated content.
 * Uploads through /api/uploads (auth required) and returns absolute URLs.
 *
 * Props:
 *   value: string[] — URLs already uploaded
 *   onChange(next: string[])
 *   maxCount (default 3)
 *   testid
 */
export default function MultiImageUploader({
  value = [],
  onChange,
  maxCount = 3,
  testid = "multi-image-uploader",
}) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const handle = async (files) => {
    if (!files || !files.length) return;
    const list = Array.from(files);
    const available = maxCount - value.length;
    if (available <= 0) {
      toast.error(`You can upload up to ${maxCount} images`);
      return;
    }
    const toUpload = list.slice(0, available);
    if (list.length > available) {
      toast.error(`Only the first ${available} file${available > 1 ? "s" : ""} will be uploaded`);
    }
    setUploading(true);
    const uploaded = [];
    try {
      for (const f of toUpload) {
        // Pre-flight validation so a 15 MB file doesn't waste
        // bandwidth just to be rejected — the toast tells the
        // admin exactly which file / why.
        const pre = validateImageFile(f);
        if (pre) {
          toast.error(`${f.name || "Image"}: ${pre}`);
          continue;
        }
        try {
          const data = await uploadImage(f);
          if (data?.url) uploaded.push(data.url);
        } catch (e) {
          toast.error(`${f.name || "Image"}: ${e.message}`);
        }
      }
      if (uploaded.length) onChange([...value, ...uploaded]);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = (idx) => {
    const next = [...value];
    next.splice(idx, 1);
    onChange(next);
  };

  return (
    <div data-testid={testid}>
      <div className="flex gap-3 flex-wrap">
        {value.map((url, idx) => (
          <div
            key={idx}
            className="relative w-24 h-24 border border-[hsl(var(--kb-border))] bg-[hsl(var(--kb-surface-alt))] overflow-hidden group"
            data-testid={`${testid}-item-${idx}`}
          >
            <img src={url} alt="" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => remove(idx)}
              className="absolute top-1 right-1 w-5 h-5 bg-black/60 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`${testid}-remove-${idx}`}
              aria-label="Remove image"
            >
              <X size={12} />
            </button>
          </div>
        ))}
        {value.length < maxCount && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            className="w-24 h-24 border border-dashed border-[hsl(var(--kb-border))] flex flex-col items-center justify-center text-[hsl(var(--kb-muted))] hover:border-[hsl(var(--kb-primary))] hover:text-[hsl(var(--kb-primary))] transition-colors"
            data-testid={`${testid}-add-btn`}
          >
            {uploading ? <Loader2 size={18} className="animate-spin" /> : <Plus size={18} />}
            <span className="text-[10px] uppercase tracking-widest mt-1">
              {uploading ? "Uploading…" : "Add image"}
            </span>
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        onChange={(e) => handle(e.target.files)}
        className="hidden"
        data-testid={`${testid}-file-input`}
      />
      <p className="text-[11px] text-[hsl(var(--kb-muted))] mt-2">
        JPG / PNG / WebP · up to {maxCount} images · max 10MB each
      </p>
    </div>
  );
}
